import { genericMemo } from '@mnd-frontend/ts';
import { getMultiColorScale } from '@mnd-frontend/ui';
import { color as d3color } from 'd3-color';
import { memoize } from 'lodash-es';
import type { ReactNode } from 'react';
import { v1 as uuid } from 'uuid';
import { Legend } from '../lib/components';
import { GraphWrapper } from '../lib/components/GraphWrapper';
import { ContextProvider } from '../lib/context';
import StackedAreas from './StackedAreas';

const getChartColor = memoize(
  (index: number, length: number): string => {
    const colorScale = getMultiColorScale(length);
    return d3color(colorScale(index) as any)!.formatHex();
  },
  (index, length) => `${index}-${length}`,
);

interface MappingsProps<T> {
  mappings: {
    name: string | ReactNode;
    dataKey: Extract<keyof Omit<T, 'name'>, string>;
    color?: string;
  }[];
  dotMappings?: {
    name: string;
    dataKey: Extract<keyof Omit<T, 'name'>, string>;
    color?: string;
  }[];
}
interface BaseProps<T> {
  data: T[];
  height?: number;
  stacked?: boolean;
  totalText?: string;
  legend?: boolean;

  staticBottomAxis?: boolean;
  onAreaClick?: (item: any) => void;
}
type Props<T> = BaseProps<T> & MappingsProps<T>;

function getColoredMappings<T>({ mappings, dotMappings }: MappingsProps<T>) {
  let i = -1;
  const length = mappings.length + (dotMappings?.length ?? 0);
  const withColorAndId = (mapping: (typeof mappings)[number]) => {
    i += 1;
    return {
      ...mapping,
      id: `${(mapping.dataKey || mapping.name) as string}_${uuid()}`,
      color: mapping.color || getChartColor(i, length),
    };
  };
  const coloredMappings = mappings.map(withColorAndId);
  const coloredDotMappings = dotMappings?.map(withColorAndId);
  return { coloredMappings, coloredDotMappings };
}

function StackedAreaChart<T>({
  data,
  height = 320,
  mappings,
  dotMappings,
  totalText,
  stacked,
  legend = true,
  staticBottomAxis,
  onAreaClick,
}: Props<T>) {
  const { coloredMappings, coloredDotMappings } = getColoredMappings({ mappings, dotMappings });
  return (
    <ContextProvider minWidth={200} height={height} selectToggle hideSelected>
      <GraphWrapper>
        {legend && <Legend items={coloredMappings} />}
        <StackedAreas
          data={data}
          mappings={coloredMappings}
          dotMappings={dotMappings ? coloredDotMappings : undefined}
          stacked={stacked}
          totalText={totalText}
          staticBottomAxis={staticBottomAxis}
          onAreaClick={onAreaClick}
        />
      </GraphWrapper>
    </ContextProvider>
  );
}

export default genericMemo(StackedAreaChart);

export const StackedAreaChartWithMonths = genericMemo(
  <T extends { month?: number }>({
    data,
    height = 320,
    mappings,
    dotMappings,
    totalText,
    stacked,
    legend = true,
    onAreaClick,
  }: Omit<Props<T>, 'staticBottomAxis'>) => {
    const { coloredMappings, coloredDotMappings } = getColoredMappings({ mappings, dotMappings });
    return (
      <ContextProvider minWidth={200} height={height} selectToggle hideSelected>
        <GraphWrapper>
          {legend && <Legend items={coloredMappings} />}
          <StackedAreas
            data={data}
            mappings={coloredMappings}
            dotMappings={dotMappings ? coloredDotMappings : undefined}
            stacked={stacked}
            totalText={totalText}
            staticBottomAxis
            includeMonthBottomAxis
            onAreaClick={onAreaClick}
          />
        </GraphWrapper>
      </ContextProvider>
    );
  },
);
